// Salgseditoren -> lagrede dokumentlinjer, og Annet salg sine linjer, gebyr og
// delbetalinger. Beregningen av belop, MVA og oppgjor ligger i otherSaleAmounts.js.

// Produktlinje fra editoren slik den lagres paa faktura og annet salg.
const editorProductLineToBackend = (line, index, { country, editor, invoiceDate }) => {
  const vat = normalizeSalesVatOption(line.vat, country);
  const productKind = line.type === 'Utleie' ? 'rental' : line.type === 'Vare' ? 'physical' : line.type === 'Abonnement' ? 'subscription' : line.productKind || undefined;
  const rentalPricing = normalizeRentalPricing(line.rentalPricingApplied || line.rentalPricing);
  const rentalFrom = line.rentalFrom || line.rentalStart || (productKind === 'rental' ? (editor.deliveryFrom || editor.serviceDate || invoiceDate) : undefined);
  const rentalTo = line.rentalTo || line.rentalEnd || (productKind === 'rental' ? (editor.deliveryTo || editor.serviceEndDate || rentalFrom) : undefined);
  return {
    id: String(line.id || `line${index}`),
    // Pekeren til fakturalinja kreditnotalinja kom fra. Uten den mistet en
    // delkreditnota sporet sitt idet brukeren lagret utkastet i editoren, og
    // samme linje kunne krediteres om igjen.
    creditedFromLineId: line.creditedFromLineId || undefined,
    lineType: 'product',
    documentLineType: 'product',
    description: line.desc || 'Uten beskrivelse',
    // Tilleggsteksten brukeren skriver paa linja. Den vises under
    // beskrivelsen i forhaandsvisningen og paa dokumentet.
    note: line.note || undefined,
    productId: line.productId || undefined,
    productNumber: line.productNumber || line.itemNumber || undefined,
    itemNumber: line.itemNumber || line.productNumber || undefined,
    productName: line.productName || line.desc || undefined,
    unit: line.unit || line.unitLabel || undefined,
    quantity: num(line.qty, 1),
    unitPrice: num(line.price),
    discountValue: num(line.disc),
    discountType: line.discMode === 'kr' ? 'amount' : 'percent',
    vatRate: vat.rate,
    vatTreatment: vat.treatment,
    salesVatCode: line.salesVatCode || vat.code || undefined,
    reportVatCode: vat.reportCode || undefined,
    salesAccountCode: line.salesAccountCode || (vat.treatment === 'salesReverseCharge51' ? '3100' : vat.treatment === 'export52' ? '3100' : '3000'),
    productKind,
    productType: line.type || undefined,
    rentalPeriod: productKind === 'rental' && rentalFrom ? { from: rentalFrom, to: rentalTo || rentalFrom, days: rentalPricingApi()?.rentalDays?.(rentalFrom, rentalTo || rentalFrom) || 1 } : undefined,
    rentalPricingApplied: productKind === 'rental' && rentalPricing.enabled ? rentalPricing : undefined,
    subscriptionPeriod: line.period || undefined,
    subscriptionFrom: line.subStart || undefined,
    subscriptionTo: line.subEnd || undefined,
  };
};

const otherSaleMoneyLabel = (value) => globalThis.PlummeI18n?.money?.(value, { decimals: 2 }) || Number(value || 0).toFixed(2);
const OTHER_SALE_ZERO_ACCOUNT_TREATMENTS = ['salesReverseCharge51', 'export52', 'zeroRated', 'exempt'];

// Linjene til et annet salg, med netto etter rabatt (og leieprising) per linje.
// Uten dem ble bare forste linjes MVA og konto brukt, og rabatten falt bort.
const otherSaleEditorLines = (editor, country = currentCountry()) => {
  const invoiceDate = editor.invDate || todayIso();
  return safeArray(editor.lines)
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => editorLineHasAmount(line) && (String(line.desc || line.description || '').trim() || num(line.price) !== 0))
    .map(({ line, index }) => {
      const mapped = editorProductLineToBackend(line, index, { country, editor, invoiceDate });
      return stripUndefinedDeep({
        ...mapped,
        salesAccountCode: line.salesAccountCode || (OTHER_SALE_ZERO_ACCOUNT_TREATMENTS.includes(mapped.vatTreatment) ? '3100' : '3000'),
        amountNet: voucherMoney(invoiceLineNet(mapped)),
      });
    });
};

// Gebyret er det betalingsformidleren trekker (kostnad, 7770). Delbetalingene er
// det som faktisk kom inn, altsa salgets brutto minus gebyret.
const otherSaleEditorPayment = (editor, { sale, paymentSource, settlementAccountCode, status }) => {
  const amounts = globalThis.PlummeOtherSaleAmounts;
  const fee = editor.feeOn ? Math.max(0, voucherMoney(num(editor.fee))) : 0;
  const paymentSplits = editor.partOn
    ? safeArray(editor.partPayments)
        .filter((payment) => num(payment.amount) > 0)
        .map((payment, index) => stripUndefinedDeep({
          id: payment.id || `split-${index + 1}-${Date.now()}`,
          amount: voucherMoney(num(payment.amount)),
          date: payment.date || editor.payDate || editor.invDate || todayIso(),
          paymentSource: normalizeOtherSalePaymentSource(payment.paymentSource || paymentSource),
          settlementAccountCode: editorSettlementAccountCode(payment.account || editor.payAccount || editor.paidTo, settlementAccountCode),
        }))
    : [];
  const payment = { settlementFeeAmount: fee, feeAmount: fee, paymentSplits };
  if (status === 'draft') return payment;
  const { gross, payout } = amounts.totals({ ...sale, ...payment });
  if (fee - gross > 0.005) throw new Error(t('sale.pay.feeTooHigh', 'Gebyret kan ikke være større enn salget ({gross}).', { gross: otherSaleMoneyLabel(gross) }));
  const paid = voucherMoney(paymentSplits.reduce((sum, split) => sum + split.amount, 0));
  if (editor.partOn && Math.abs(paid - payout) > 0.005) {
    throw new Error(t('sale.pay.splitMismatchError', 'Delbetalingene ({paid}) må til sammen stemme med salgstotalen minus gebyr ({expected}).', { paid: otherSaleMoneyLabel(paid), expected: otherSaleMoneyLabel(payout) }));
  }
  return payment;
};

// Det som skal komme inn for salget i editoren: samme avrunding som foringen.
const otherSaleEditorExpectedPayout = (editor) => {
  const lines = otherSaleEditorLines(editor);
  const sale = { lines, amountNet: voucherMoney(lines.reduce((sum, line) => sum + line.amountNet, 0)), settlementFeeAmount: editor.feeOn ? num(editor.fee) : 0 };
  return globalThis.PlummeOtherSaleAmounts.totals(sale).payout;
};

// Kvitteringen viser de samme linjene som ble bokfort. Eldre salg har en linje.
const otherSaleReceiptLines = (sale) => {
  const lines = globalThis.PlummeOtherSaleAmounts.storedLines(sale);
  if (lines.length) return lines.map((line) => stripUndefinedDeep({ ...line, vatRate: line.vatTreatment && line.vatTreatment !== 'standard' ? 0 : num(line.vatRate) }));
  return [{
    id: `${sale.id}-line`,
    description: sale.description || 'Annet salg',
    quantity: 1,
    unitPrice: num(sale.amountNet),
    vatRate: String(sale?.vatTreatment || '') === 'standard' ? num(sale?.vatRate) : 0,
  }];
};

window.PlummeOtherSaleEditor = { expectedPayout: otherSaleEditorExpectedPayout };
